import { useRxData } from 'rxdb-hooks';
import { ISiteSummaryList } from '@components/siteSummary';
import { MangoQuery, RxCollection, RxDocument } from 'rxdb/dist/types/types';
import { useCallback } from 'react';
import { CurrentSiteStage } from '@components/siteSummary/components';
import { useRxCountQuery } from '@hooks/useCount';

export type IUseSiteSummaryReturn = {
	result: RxDocument<ISiteSummaryList>[];
	isFetching: boolean;
	fetchMore: () => void;
	totalCount: number;
};

export interface ISiteSummaryFilterProps {
	name?: string;
	sortBy?: string;
	current_stage_of_site_c?: string;
	lastactivitydate?: [null | Date, null | Date];
	competition_site?: boolean;
	adopted_site?: boolean;
	ssm_site?: boolean;
	visited_by?: string;
	createddate?: [null | Date, null | Date];
	sitenumber?: string;
	mdicode_c?: string;
}

export const useSiteSummaryRxDb = (props: ISiteSummaryFilterProps): IUseSiteSummaryReturn => {
	const {
		name = '',
		current_stage_of_site_c = '',
		lastactivitydate,
		competition_site,
		adopted_site,
		ssm_site,
		sortBy,
		visited_by,
		createddate,
		sitenumber,
		mdicode_c,
	} = props;
	const queryConstructor = useCallback(
		(collection: RxCollection<ISiteSummaryList>) => {
			let querySelector: MangoQuery<ISiteSummaryList> = {
				selector: {
					$or: [
						{
							contract_name: { $regex: `.*${name}.*`, $options: 'i' },
						},
						{
							sitenumber_c: { $regex: `.*${name}.*`, $options: 'i' },
						},
						{
							name: { $regex: `.*${name}.*`, $options: 'i' },
						},
						{
							membership_no: { $regex: `.*${name}.*`, $options: 'i' },
						},
					],
				},
				sort: [{ createddate: 'desc' }],
			};

			if (querySelector.selector && current_stage_of_site_c) {
				if (current_stage_of_site_c === CurrentSiteStage.New_Lead) {
					querySelector.selector.$or = [
						{
							current_stage_of_site_c: { $regex: `.*${CurrentSiteStage.DRAFT}.*`, $options: 'i' },
						},
						{
							current_stage_of_site_c: { $regex: `.*${current_stage_of_site_c}.*`, $options: 'i' },
						},
					];
				} else {
					querySelector.selector.current_stage_of_site_c = { $regex: `.*${current_stage_of_site_c}.*`, $options: 'i' };
				}
			}

			if (querySelector.selector && competition_site) {
				querySelector.selector.competition_site = competition_site;
			}

			if (querySelector.selector && adopted_site) {
				querySelector.selector.adopted_site = adopted_site;
			}

			if (querySelector.selector && ssm_site) {
				querySelector.selector = {
					...querySelector.selector,
					primary_cmdi: {
						$ne: null,
						$exists: true,
						$nin: [null, ''],
					},
					source_of_site_lead_c: { $eq: 'LMS' },
				};
			}

			if (querySelector.selector && mdicode_c) {
				querySelector.selector.$or = [
					{
						cmdi_code_c: mdicode_c,
					},
					{
						fetchcmdi_c: mdicode_c,
					},
				];
			}

			if (querySelector.selector && lastactivitydate) {
				const [startDate, endDate] = lastactivitydate;
				if (startDate && endDate) {
					querySelector.selector.lastactivitydate = {
						$gte: startDate.toISOString(),
						$lte: endDate.toISOString(),
					};
				}
			}

			if (querySelector.selector && createddate) {
				const [startDate, endDate] = createddate;
				if (startDate && endDate) {
					querySelector.selector.createddate = {
						$gte: startDate.toISOString(),
						$lte: endDate.toISOString(),
					};
				}
			}

			if (querySelector.selector && name) {
				querySelector.selector.$or = [
					{
						contract_name: { $regex: `.*${name}.*`, $options: 'i' },
					},
					{
						sitenumber_c: { $regex: `.*${name}.*`, $options: 'i' },
					},
					{
						name: { $regex: `.*${name}.*`, $options: 'i' },
					},
					{
						membership_no: { $regex: `.*${name}.*`, $options: 'i' },
					},
				];
			}

			if (querySelector.selector && visited_by) {
				querySelector.selector.$or = [
					{
						visited_by: { $regex: `.*${visited_by}.*`, $options: 'i' },
					},
				];
			}
			if (querySelector.selector && sitenumber) {
				querySelector.selector.sitenumber_c = sitenumber;
			}

			if (sortBy) {
				querySelector = {
					...querySelector,
					sort: [{ [sortBy]: 'desc' }, { createddate: 'desc' }],
				};
			}
			return collection.find(querySelector);
		},
		[
			adopted_site,
			competition_site,
			current_stage_of_site_c,
			lastactivitydate,
			name,
			sortBy,
			visited_by,
			ssm_site,
			mdicode_c,
		]
	);
	const totalCount = useRxCountQuery<ISiteSummaryList>('site_summary_list', queryConstructor);

	const { result, isFetching, fetchMore } = useRxData<ISiteSummaryList>('site_summary_list', queryConstructor, {
		pageSize: 20,
		pagination: 'Infinite',
	});

	return {
		result,
		isFetching,
		fetchMore,
		totalCount,
	};
};
